import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { GET_RECORDINGS_BY_EVENT } from "../../../../rest/queries";
import { GetRecordingsByEventParams } from "../../../../common/types/recordings/params";
import { RecordingsApiResponse } from "../../../../common/types/recordings/api";
import { buildEventQueryVariables } from "../../../../common/utils/buildEventQueryVariables";
import { errorNotification } from "../../../../common/variables/notification";
import { RecordingViewModel } from "../../models/recordingViewModel";
import { mapRecordingEntityToViewModel } from "../../adapters/recordingAdapter";

export const useGetRecordingsByEvent = (params: GetRecordingsByEventParams) => {
  const skip = !params.customerId || !params.eventTimestamp;

  const variables = buildEventQueryVariables(params);

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    recordings: RecordingsApiResponse;
  }>(GET_RECORDINGS_BY_EVENT, {
    variables,
    skip,
    fetchPolicy: "cache-first",
    onError: error => {
      console.error("GetRecordingsByEvent:", error);

      errorNotification();
    },
  });

  const recordings = useMemo<RecordingViewModel[]>(
    () => data?.recordings?.data.map(mapRecordingEntityToViewModel) ?? [],
    [data]
  );

  const lastEvaluatedKey = data?.recordings?.lastEvaluatedKey;

  const fetchNextPage = async () => {
    if (!lastEvaluatedKey) return;

    try {
      await fetchMore({
        variables: {
          ...variables,
          lastEvaluatedKey: encodeURIComponent(
            JSON.stringify(lastEvaluatedKey)
          ),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            ...fetchMoreResult,
            recordings: {
              ...fetchMoreResult.recordings,
              data: [
                ...(prev.recordings?.data ?? []),
                ...(fetchMoreResult.recordings?.data ?? []),
              ],
              lastEvaluatedKey: fetchMoreResult.recordings?.lastEvaluatedKey,
            },
          };
        },
      });
    } catch (e) {
      errorNotification("Error fetching next page of recordings");

      console.error("Error fetching next page of recordings:", e);
    }
  };

  return {
    recordings,
    loading,
    error,
    refetch,
    fetchMore,
    lastEvaluatedKey,
    fetchNextPage,
  } as const;
};
